import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import PropTypes from 'prop-types';
import ContextButton from '../../components/ContextButton';
import SelectableList from '../../components/SelectableList';
import ViewOption from '../../components/view/ViewOption';
import appTypes from '../../state/modules/app/types';
import { viewActions } from '../../state/modules/view';
import { serviceActions } from '../../state/modules/service';
import componentStyles from 'styles/partials/_viewcontroller.css';
import btnStyles from 'styles/partials/_button.css';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import getIn from '../connectedForm/utils/getIn';
import { InfoButton, SuccessButton } from '../connectedForm/Button';
import { handleComponentQueryError } from '../../utils/parseErrorResponse';
import { useDispatch, useSelector } from 'react-redux';
import ViewContext from '../Context/ViewContext';
import getValue from '../../components/connectedForm/utils/getValue';
// @ts-ignore
import { Analytics } from 'mixpanel';
import getClientMutationId from '../../utils/getClientMutationId';
import { FILTER_TYPES } from '../../utils/FilterHelper';
import {
  AlertType,
  IFilterAttributeBigDecimal,
  IFilterAttributeDate,
  IFilterAttributeLong,
  IFilterAttributeObject,
  IFilterAttributeString,
  IFilterBigDecimal,
  IFilterBoolean,
  IFilterDate,
  IFilterDateTime,
  IFilterLong,
  IFilterObject,
  IFilterString,
  ISystemUser,
  IViewInput,
  IViewMutationResult,
  IViewMutationVariables,
  Maybe,
} from '../../graphql/schema/p4types';
import { ViewMutation } from '../../graphql/mutations/view/ViewsMutation.graphql';
import DeprecatedConfirmationPrompt from '../modals/ConfirmationPrompt';
import { EntityType } from '../../@types/entityTypes';
import { FetchResult } from '@apollo/client/link/core';
import { MutationFunctionOptions } from '@apollo/client/react/types/types';
import FluentModal from '../modals/FluentModal';

type IUseDeleteViewOption = (
  viewScreenName: string,
  refreshViews: () => void
) => [(viewId: number) => void, ReactElement];

const useDeleteViewOption: IUseDeleteViewOption = (viewScreenName, refreshViews) => {
  const [deleteConfirmationModalState, setDeleteConfirmationModalState] = useState<{
    modalOpen: boolean;
    viewId?: number;
    viewConcurrencyVersion?: number;
    viewName?: string;
  }>({ modalOpen: false });
  const dispatch = useDispatch();
  const views = useSelector((store: any) => store.views[viewScreenName]);

  const [viewUpdate] = useMutation<IViewMutationResult, IViewMutationVariables>(ViewMutation);

  const onClose = () => {
    //Keeping all the state but the 'modalOpen' to avoid seeing a 'flicker' when the modal closes - For some reason it stays open for one render-cycle before closing.
    setDeleteConfirmationModalState((confirmationModalState) => ({ ...confirmationModalState, modalOpen: false }));
  };

  const handleRequestDelete = (viewId: number) => {
    const viewName = Object.keys(views.view).find((viewName) => views.view[viewName].id === viewId);
    const concurrencyVersion = views.view[viewName!].concurrencyVersion;

    setDeleteConfirmationModalState({
      modalOpen: true,
      viewId: viewId,
      viewConcurrencyVersion: concurrencyVersion,
      viewName: viewName,
    });
  };

  const onDelete = () => {
    return viewUpdate({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          view: {
            id: deleteConfirmationModalState.viewId,
            concurrencyVersion: deleteConfirmationModalState.viewConcurrencyVersion,
            deleted: true,
          },
        },
      },
    })
      .then((response) => {
        if (!response.data?.viewUpdate?.result?.name) {
          dispatch(viewActions.deleteViewSuccess(viewScreenName, deleteConfirmationModalState.viewId));
          refreshViews();
          Analytics.track('Workspace view deleted', { screenName: viewScreenName });
        } else {
          dispatch(serviceActions.genericAlert('View could not be deleted', 'An error occurred deleting the view'));
        }
        onClose();
      })
      .catch(() => {
        dispatch(serviceActions.genericAlert('View could not be deleted', 'An error occurred deleting the view'));
        onClose();
      });
  };

  return [
    handleRequestDelete,
    <FluentModal isOpen={deleteConfirmationModalState.modalOpen}>
      <div style={{ padding: '1em' }}>
        <DeprecatedConfirmationPrompt
          onReject={onClose}
          confirm={onDelete}
          title="Confirmation Required"
          message={`Are you sure you want to delete the view named ${deleteConfirmationModalState.viewName}?`}
        />
      </div>
    </FluentModal>,
  ];
};

type IViewFilter = {
  fieldName: string;
  position: any;
  label?: Maybe<string>;
  id?: number;
  deleted?: boolean;
  filterType?: any;
  containsText?: Maybe<string>;
  yes?: Maybe<boolean>;
  no?: Maybe<boolean>;
  codes?: Maybe<Array<any>>;
  filterStyle?: any;
  from?: any;
  to?: any;
  dateType?: any;
  dateRelative?: any;
  financialCalendar?: Maybe<boolean>;
  numberOfPeriods?: number;
};

type IOption = {
  onDelete: (viewId: number) => void;
  shared: any;
  isDefault: boolean;
  concurrencyVersion: any;
  description: any;
  onSetAsDefault: (viewId: number) => void;
  id: any;
  type: any;
  dashboard: any;
};

type IViewControllerProps = {
  user: ISystemUser;
  refreshViews: () => void;
  viewUpdate: (
    options?: MutationFunctionOptions<IViewMutationResult, IViewMutationVariables>
  ) => Promise<FetchResult<IViewMutationResult>>;
  currentUserId: number;
  onDeleteComplete: () => void;
  allowViewUpdates: boolean;
  customViewConfigComponent: any;
  embeddedView: boolean;
  onSave?: () => void;
  viewName: string;
  tabIndex: number | string;
  readOnly: boolean;
  type?: 'DASHBOARD';
  initialize: (option: IOption) => void;
  currentView: any;
  dashboardViews: Array<any>;
};

type IViewFilterType =
  | IFilterBigDecimal
  | IFilterBoolean
  | IFilterObject
  | IFilterDate
  | IFilterDateTime
  | IFilterLong
  | IFilterString
  | IFilterAttributeBigDecimal
  | IFilterAttributeObject
  | IFilterAttributeDate
  | IFilterAttributeLong
  | IFilterAttributeString;

// eslint-disable-next-line complexity
const ViewController = ({
  user,
  refreshViews,
  viewUpdate,
  currentUserId,
  onDeleteComplete,
  allowViewUpdates,
  customViewConfigComponent,
  embeddedView,
  onSave,
  viewName,
  tabIndex,
  readOnly,
  type,
  initialize,
  currentView,
  dashboardViews,
}: IViewControllerProps) => {
  const contextButtonRef = useRef<any>();

  const dispatch = useDispatch();
  const { viewScreenName } = useContext(ViewContext);

  const allViews = useSelector((store: any) => store.views);
  const screenViews = type === 'DASHBOARD' ? dashboardViews : allViews?.[viewScreenName];

  const currentViewName = screenViews.currentView.name;
  const [inputValue, setInputValue] = useState(currentViewName === 'default' ? '' : currentViewName);

  useEffect(() => {
    setInputValue(currentViewName === 'default' ? '' : currentViewName);
  }, [currentViewName]);

  const styles: any = {};
  Object.assign(styles, componentStyles, btnStyles);

  const switchView = (option: IOption) => {
    contextButtonRef.current.popoutClose();

    if (type === 'DASHBOARD') {
      initialize(option.dashboard);
    } else {
      dispatch(viewActions.switchView(viewScreenName, option.description));
      Analytics.track('Workspace view switched', {
        screenName: viewScreenName,
        description: option.description,
      });
    }
  };

  const saveView = () => {
    const newViewName = inputValue.trim();

    const alertAction = saveViewValidate(screenViews, newViewName);
    if (alertAction) {
      return dispatch(alertAction);
    }

    // If updating a view add id/concurrency details for any details that were originally included in the view
    // and record any details that have been removed
    const itemTypes: Array<keyof IViewInput> = FILTER_TYPES.slice();
    itemTypes.push('columns');
    itemTypes.push('orderBy');
    let viewClone: IViewInput = Object.assign({}, screenViews.currentView);

    const origView = screenViews.view[newViewName];

    if (origView) {
      let origItem;
      itemTypes.forEach((itemType) => {
        if (!origView[itemType]) {
          return;
        }

        // Add id and concurrency for any details that were present in original version of view
        const included: { [fieldName: string]: boolean } = {};
        let newItem: IViewFilterType;
        if (viewClone[itemType]) {
          viewClone[itemType] = viewClone[itemType].slice();
          viewClone[itemType].forEach((curItem: IViewFilterType, index: number) => {
            included[curItem.fieldName!] = true;
            origItem = origView[itemType].find((item: IViewFilterType) => {
              return item.fieldName === curItem.fieldName;
            });
            if (!origItem) {
              return;
            }

            newItem = Object.assign({}, curItem);
            viewClone[itemType][index] = newItem;

            newItem.id = origItem.id;
            newItem.concurrencyVersion = origItem.concurrencyVersion;
          });
        }

        // Remove any original details that have not been kept
        if (origView[itemType]) {
          origView[itemType].forEach((origItem: IViewFilterType) => {
            if (!included[origItem.fieldName!]) {
              origItem.deleted = true;
              newItem = Object.assign({}, origItem);
              newItem.deleted = true;
              if (!viewClone[itemType]) viewClone[itemType] = [];

              const updateIndex = viewClone[itemType].findIndex((item: IViewFilterType) => item.id === newItem.id);
              viewClone[itemType][updateIndex >= 0 ? updateIndex : viewClone[itemType].length] = newItem;
            }
          });
        }
      });
    } else if (viewClone.name) {
      viewClone = Object.assign({}, viewClone);
      delete viewClone.id;
      delete viewClone.concurrencyVersion;
      delete viewClone.uuid;

      itemTypes.forEach((itemType) => {
        if (viewClone[itemType]) {
          const newView = {} as IViewInput;
          newView[itemType] = [];

          // Clear any original details for id and concurrency
          viewClone[itemType].forEach((item: IViewFilterType, index: number) => {
            const newItem = Object.assign({}, item);
            delete newItem.id;
            delete newItem.concurrencyVersion;
            delete newItem.uuid;
            delete (newItem as any).fieldColumn; // deleting as causes stack overflow in normalizeVariables in certain conditions 😕
            newView[itemType][index] = newItem;
          });
          viewClone[itemType] = newView[itemType];
        }
      });
    }
    viewClone.screen = viewScreenName;
    viewClone.application = appTypes.APPLICATION_ID;
    viewClone.systemUserId = user.id;
    viewClone.name = newViewName;
    viewClone.primaryView = true;
    const columns: Array<any> = [];

    viewClone.columns?.map((column) => {
      const col = {
        position: column!.position,
        width: column!.width ? column!.width.toFixed(0) : null,
        fieldName: column!.fieldName,
        id: column!.id,
        deleted: column!.deleted,
        name: column!.title ?? null,
        title: column!.title,
        total: !!column!.total,
        totalCalculation: column!.totalCalculation,
      };
      columns.push(col);
    });
    viewClone.columns = columns;
    const variables = normalizeVariables(viewClone);

    // Clean the filters
    FILTER_TYPES.forEach((filterType) => {
      if (variables[filterType]) {
        const filters: Array<IViewFilter> = [];
        // eslint-disable-next-line complexity
        variables[filterType as keyof IViewInput].forEach((filter: IViewFilterType) => {
          const newFilter: IViewFilter = {
            fieldName: filter.fieldName!,
            position: filter.position,
            label: filter.label,
          };

          if (filter.id) newFilter.id = filter.id;
          if (filter.deleted) newFilter.deleted = filter.deleted;
          switch (filterType) {
            case 'filterStrings':
            case 'filterAttributeStrings':
              newFilter.filterType = (filter as IFilterString).filterType;
              newFilter.containsText = (filter as IFilterString).containsText;
              break;
            case 'filterBooleans':
              newFilter.yes = (filter as IFilterBoolean).yes;
              newFilter.no = (filter as IFilterBoolean).no;
              break;
            case 'filterObjects':
            case 'filterAttributeObjects':
              newFilter.codes = (filter as any).objects ? (filter as any).objects.map((obj: any) => obj.id) : [];
              newFilter.filterStyle = filter.filterStyle;
              break;
            case 'filterEnums':
              if (Array.isArray((filter as IFilterObject).codes)) {
                newFilter.codes = (filter as IFilterObject).codes;
              } else {
                newFilter.codes = (filter as IFilterObject).codes ? [(filter as any).codes.value] : null;
              }
              break;
            case 'filterDates':
            case 'filterDateTimes':
            case 'filterAttributeDates':
              newFilter.from = (filter as IFilterDate).from ? (filter as IFilterDate).from : null;
              newFilter.to = (filter as IFilterDate).to ? (filter as IFilterDate).to : null;
              newFilter.dateType = (filter as IFilterDate).dateType;
              newFilter.dateRelative = (filter as IFilterDate).dateRelative;
              newFilter.financialCalendar = (filter as IFilterDate).financialCalendar;
              newFilter.numberOfPeriods = (filter as IFilterDate).numberOfPeriods;
              break;
            case 'filterLongs':
            case 'filterAttributeLongs':
            case 'filterBigDecimals':
            case 'filterAttributeBigDecimals':
              newFilter.filterType = (
                filter as IFilterLong | IFilterAttributeLong | IFilterBigDecimal | IFilterAttributeBigDecimal
              ).filterType;
              newFilter.from = (
                filter as IFilterLong | IFilterAttributeLong | IFilterBigDecimal | IFilterAttributeBigDecimal
              ).from
                ? (filter as IFilterLong | IFilterAttributeLong | IFilterBigDecimal | IFilterAttributeBigDecimal).from
                : null;
              newFilter.to = (
                filter as IFilterLong | IFilterAttributeLong | IFilterBigDecimal | IFilterAttributeBigDecimal
              ).to
                ? (filter as IFilterLong | IFilterAttributeLong | IFilterBigDecimal | IFilterAttributeBigDecimal).to
                : null;
              break;
          }
          filters.push(newFilter);
        });
        variables[filterType] = filters;
      }
    });

    Analytics.track('Workspace view saved', { screenName: viewScreenName, description: newViewName });

    viewUpdate({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          view: variables,
        },
      },
    })
      .then(({ data }) => {
        if (data?.viewUpdate?.result) {
          dispatch(viewActions.saveView(viewScreenName, newViewName, data.viewUpdate.result));
          refreshViews();
        } else {
          dispatch(serviceActions.genericAlert('View could not be saved', 'An error occurred processing the view'));
        }
      })
      .catch((error: any) => {
        handleComponentQueryError(
          error,
          { dispatch, name: 'View', entity: { type: 'View' } as EntityType },
          'View could not be saved'
        );
      });
    contextButtonRef.current.popoutClose();
  };

  const saveViewValidate = (viewsToValidate: any, newViewName: string) => {
    if (newViewName === 'default') {
      return serviceActions.genericAlert(
        'View could not be updated',
        'The default view cannot be overwritten, you must save the view with a different name'
      );
    }
    if (viewsToValidate.currentView.name !== newViewName && viewsToValidate.view[newViewName]) {
      return serviceActions.genericAlert(
        'View could not be updated',
        `A view with the requested name (${newViewName}) already exists, you cannot overwrite an existing view with a different one`
      );
    }
    if (newViewName === '') {
      return serviceActions.genericAlert('View could not be updated', 'Please enter a name to use for saving the view');
    }

    return null;
  };

  const handleSetAsDefault = (viewId: number) => {
    dispatch(
      viewActions.setDefaultView(viewScreenName, viewId, viewUpdate, () => {
        refreshViews();
      })
    );
  };

  const [handleRequestDelete, deleteViewModals] = useDeleteViewOption(viewScreenName, refreshViews);

  const toggleHideConfiguration = () => {
    dispatch(viewActions.toggleHideConfiguration(viewScreenName));
  };

  const options: Array<IOption> = [],
    sharedOptions: Array<IOption> = [];
  let view;
  Object.keys(screenViews.view).forEach((key) => {
    view = screenViews.view[key];

    if ((view.name === 'default' && Object.keys(screenViews.view).length === 1) || view.screen === viewScreenName) {
      let description = view.name;
      if (type === 'DASHBOARD' && view.shared) {
        description += ` (${view.companyUser.name})`;
      }

      const option = {
        id: view.id,
        concurrencyVersion: view.concurrencyVersion,
        description: description,
        isDefault: screenViews.defaultViewId === view.id,
        onSetAsDefault: handleSetAsDefault,
        onDelete: handleRequestDelete,
        shared: view.shared,
        dashboard: view,
        type: type,
      };

      //Views shared by other users go in separate section.
      if (view.shared && view.companyUser.id !== currentUserId) {
        sharedOptions.push(option);
      } else {
        options.push(option);
      }
    }
  });

  const myOptionsText = type === 'DASHBOARD' ? 'My Dashboards' : ' Select View';
  const sharedOptionsText = type === 'DASHBOARD' ? 'Shared Dashboards' : 'Select Shared View';

  const alertUpdated = (result: any) => {
    const dashboardName = getIn(result, 'dashboardSetPrimary.result.name');
    dispatch(
      serviceActions.genericAlert(
        'Default Dashboard',
        `Dashboard '${dashboardName}' has been set as default`,
        AlertType.Info
      )
    );
  };

  const sharedOptionsComponent =
    sharedOptions.length > 0 ? (
      <>
        <div className={styles.subtitle}>{sharedOptionsText}</div>
        <SelectableList
          optionClass={ViewOption}
          onSelect={switchView}
          options={sharedOptions}
          deletable={false}
          onMutationComplete={alertUpdated}
        />
      </>
    ) : null;

  //'customViewConfigComponent' is rendered in DashboardForm because it's a linked field (Form.Field).
  //(Which is a problem because the <input> component submits the form and refreshes the page, which we don't want to do).
  const viewConfigComponent = customViewConfigComponent || (
    <>
      <div className={styles.subtitle}>Save View As</div>
      <input
        className={`${styles.textfield} ignore-react-onclickoutside`}
        type="text"
        value={inputValue}
        onChange={(value) => setInputValue(getValue(value))}
        data-testid="save_view_input"
        style={{ width: '165px' }}
      />
    </>
  );

  let saveButtonLabel = 'Save';
  if (getIn(currentView, 'companyUser.id') !== currentUserId) {
    saveButtonLabel = 'Save a Copy';
  }

  //If a workspace is embedded in a form, then lowercase button will trigger a form submit and refresh the page.
  //We do not want it to do that! Therefore use uppercase Button (styled div) for this scenario.
  const Button = onSave ? SuccessButton : InfoButton;
  const saveButton = (
    <Button
      className={styles[onSave ? 'btnSuccessSmall' : 'btnPrimarySmall']}
      onClick={onSave || saveView}
      data-testid="save_view_button"
      style={{ display: 'inline-block' }}
    >
      {saveButtonLabel}
    </Button>
  );

  let optionIcon: Maybe<'visibility' | 'visibility_off'> = null;
  if (!(embeddedView || !allowViewUpdates)) {
    optionIcon = screenViews.currentView.hideConfiguration ? 'visibility' : 'visibility_off';
  }

  return (
    <div className="viewContextButton" style={{ paddingTop: type === 'DASHBOARD' ? '5px' : '4px' }}>
      {deleteViewModals}
      <ContextButton
        ref={contextButtonRef}
        containerClass="flexContainer"
        buttonOuterClass="flexBtnContainer"
        label={`${type === 'DASHBOARD' ? 'Dashboard' : 'View'} (${screenViews.currentView.name || viewName})`}
        options={options}
        optionIcon={optionIcon}
        optionTooltip="Toggle Filter Toolbar"
        onOptionIconClick={embeddedView ? null : toggleHideConfiguration}
        tabIndex={tabIndex}
        alignLeft={true}
      >
        <div className={styles[type === 'DASHBOARD' ? 'wideDropdownContainer' : 'dropdownContainer']}>
          <div className={styles.subtitle}>{myOptionsText}</div>
          <SelectableList
            optionClass={ViewOption}
            onSelect={switchView}
            options={options}
            onDeleteComplete={onDeleteComplete}
            onMutationComplete={alertUpdated}
            deletable={allowViewUpdates}
            data-testid="view-options"
          />
          {sharedOptionsComponent}
          <div>
            {allowViewUpdates ? viewConfigComponent : null}
            {readOnly || !allowViewUpdates ? null : saveButton}
          </div>
        </div>
      </ContextButton>
    </div>
  );
};

ViewController.propTypes = {
  embeddedView: PropTypes.bool,
  allowViewUpdates: PropTypes.bool,
};

ViewController.defaultProps = {
  embeddedView: false,
  allowViewUpdates: true,
};

export default ViewController;
